export type DeploymentEnvironment =
  | "development"
  | "test"
  | "preview"
  | "staging"
  | "production";

export type DeploymentProvider = "local" | "cloudflare";

export interface DeploymentContext {
  buildId: string;
  canonicalOrigin: string;
  deploymentOrigin?: string;
  environment: DeploymentEnvironment;
  provider: DeploymentProvider;
  releaseSha: string;
}

export type Environment = Record<string, string | undefined>;

export const UNRELEASED_SHA = "development";

function readOptionalEnv(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeEnvironment(
  value: string | undefined,
): DeploymentEnvironment {
  const normalized = readOptionalEnv(value);
  if (normalized === undefined) return "development";
  if (normalized === "development") return "development";
  if (normalized === "production") return "production";
  if (normalized === "preview") return "preview";
  if (normalized === "stage" || normalized === "staging") return "staging";
  if (normalized === "test") return "test";
  throw new Error(`Unsupported deployment environment: ${normalized}`);
}

function deploymentProvider(environment: Environment): DeploymentProvider {
  const declared = readOptionalEnv(environment.REPACKIFY_DEPLOYMENT_PROVIDER);
  if (declared === "cloudflare") return "cloudflare";
  if (declared !== undefined) {
    throw new Error(`Unsupported deployment provider: ${declared}`);
  }
  if (environment.CF_WORKER_NAME) return "cloudflare";
  return "local";
}

function deploymentOrigin(environment: Environment): string | undefined {
  if (environment.REPACKIFY_DEPLOYMENT_ORIGIN) {
    return environment.REPACKIFY_DEPLOYMENT_ORIGIN;
  }
  return undefined;
}

function buildId(
  releaseSha: string,
  environmentName: DeploymentEnvironment,
  previewNumber: string | undefined,
): string {
  const qualifier =
    environmentName === "preview" && previewNumber
      ? `${environmentName}-${previewNumber}`
      : environmentName;
  return `${releaseSha}-${qualifier}`;
}

export function readDeploymentContext(
  environment: Environment,
): DeploymentContext {
  const provider = deploymentProvider(environment);
  const environmentName = normalizeEnvironment(
    readOptionalEnv(environment.REPACKIFY_ENVIRONMENT) ??
      readOptionalEnv(environment.NEXT_PUBLIC_ENVIRONMENT) ??
      readOptionalEnv(environment.NODE_ENV),
  );
  const releaseSha =
    readOptionalEnv(environment.REPACKIFY_RELEASE_SHA) ??
    readOptionalEnv(environment.GITHUB_SHA) ??
    readOptionalEnv(environment.NEXT_PUBLIC_RELEASE_SHA) ??
    UNRELEASED_SHA;

  return {
    buildId: buildId(releaseSha, environmentName, readOptionalEnv(environment.REPACKIFY_PREVIEW_NUMBER)),
    canonicalOrigin: environment.NEXT_PUBLIC_APP_URL ?? "https://repackify.com",
    deploymentOrigin: deploymentOrigin(environment),
    environment: environmentName,
    provider,
    releaseSha,
  };
}
